/**
 * View: Reward Catalog
 * Basic reward catalog screen with table and pagination.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableLoadingRow,
  TableRow,
} from "@/components/ui/table/table";
import { MoreIcon } from "@/assets/svg";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";
import { SuccessModal } from "@/components/ui/success-modal";
import type { VoucherListItem } from "../../domain/model/RewardCatalogModel";
import {
  RewardCatalogViewModel,
  type PagingInfo,
} from "../viewmodel/RewardCatalogViewModel";

const PAGE_SIZE = 10;

const formatDate = (isoDate: string): string => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  return date.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getStatusBadgeClass = (status: string): string => {
  switch (status.toLowerCase()) {
    case "active":
      return "bg-green-100 text-green-800";
    case "inactive":
      return "bg-gray-100 text-gray-800";
    case "expired":
      return "bg-red-100 text-red-800";
    case "draft":
      return "bg-yellow-100 text-yellow-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const RewardCatalogView = (): React.JSX.Element => {
  usePageTitle("Reward Catalog");

  const navigate = useNavigate();
  const [items, setItems] = useState<VoucherListItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [paging, setPaging] = useState<PagingInfo>({
    currentPage: 1,
    pageSize: PAGE_SIZE,
    totalPages: 1,
    totalRecords: 0,
  });
  const [openRowId, setOpenRowId] = useState<string | null>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpenRowId(null), 200);
  }, [cancelClose]);

  const handleDropdownEnter = (
    e: React.MouseEvent<HTMLButtonElement>,
    id: string,
  ) => {
    cancelClose();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + window.scrollY + 4,
      right: window.innerWidth - rect.right,
    });
    setOpenRowId(id);
  };

  // Delete modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<VoucherListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false);

  const { loadCatalog, deleteVoucher } = RewardCatalogViewModel({
    setItems,
    setIsLoading,
    setPaging,
    setError,
  });

  const fetchData = useCallback(
    (page: number, search?: string) => {
      void loadCatalog({
        page,
        pageSize: PAGE_SIZE,
        search: search || undefined,
      });
    },
    [loadCatalog]
  );

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    fetchData(1, value);
  };

  const handlePageChange = (page: number) => {
    fetchData(page, searchQuery);
  };

  const handleDeleteClick = (item: VoucherListItem) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
    setOpenRowId(null);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);
    const success = await deleteVoucher(itemToDelete.voucherId);
    setIsDeleting(false);

    if (success) {
      setDeleteModalOpen(false);
      setItemToDelete(null);
      setShowDeleteSuccessModal(true);
    } else {
      // Error is already set by the ViewModel
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteSuccessClose = () => {
    setShowDeleteSuccessModal(false);
    // Refetch data after successful deletion
    fetchData(paging.currentPage, searchQuery);
  };

  return (
    <MainLayout title="Reward Catalog">
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center gap-4">
          <div className="flex-1 max-w-80">
            <SearchInput
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search by title or brand"
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate("/swarapoint/reward-catalog/create")}
            >
              + Add New
            </Button>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Voucher ID</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead>Points</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Valid From</TableHead>
              <TableHead>Valid Until</TableHead>
              <TableHead>Created By</TableHead>
              <TableHead> </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableLoadingRow
                colSpan={10}
                message="Loading reward catalog..."
              />
            ) : items.length === 0 ? (
              <TableEmptyRow colSpan={10} message="No Reward Catalog found." />
            ) : (
              items.map((item) => (
                <TableRow key={item.voucherId}>
                  <TableCell className="font-mono text-sm">
                    {item.voucherId}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {item.title}
                      {item.badgeText && (
                        <span
                          className="px-2 py-0.5 text-xs font-medium rounded"
                          style={{
                            backgroundColor: item.badgeColor || "#0066FF",
                            color: "#FFFFFF",
                          }}
                        >
                          {item.badgeText}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{item.brandName}</TableCell>
                  <TableCell>
                    {item.pointsRequired.toLocaleString("id-ID")}
                  </TableCell>
                  <TableCell>
                    {item.remainingStock.toLocaleString("id-ID")} /{" "}
                    {item.totalStock.toLocaleString("id-ID")}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusBadgeClass(
                        item.status
                      )}`}
                    >
                      {item.status}
                    </span>
                  </TableCell>
                  <TableCell>{formatDate(item.validFrom)}</TableCell>
                  <TableCell>{formatDate(item.validUntil)}</TableCell>
                  <TableCell>{item.createdBy}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<MoreIcon />}
                      aria-label={`More actions for voucher ${item.voucherId}`}
                      onMouseEnter={(e) => handleDropdownEnter(e, item.voucherId)}
                      onMouseLeave={scheduleClose}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {openRowId && (
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: dropdownPos.top,
              right: dropdownPos.right,
            }}
            className="w-32 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            <button
              onClick={() => {
                navigate(`/swarapoint/reward-catalog/edit/${openRowId}`);
                setOpenRowId(null);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Edit
            </button>
            <button
              onClick={() => {
                const item = items.find((d) => d.voucherId === openRowId);
                if (item) handleDeleteClick(item);
              }}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 transition-colors"
            >
              Delete
            </button>
          </div>
        )}

        <Pagination
          currentPage={paging.currentPage}
          totalPages={paging.totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Reward"
        message={`Are you sure you want to delete "${itemToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
        variant="danger"
      />

      {/* Delete Success Modal */}
      <SuccessModal
        isOpen={showDeleteSuccessModal}
        onClose={handleDeleteSuccessClose}
        title="Deleted"
        message="Reward has been deleted successfully."
      />
    </MainLayout>
  );
};
